import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { monthDates, toDateStr } from "./shiftUtils";
import { useRosterData } from "./useRosterData";
import { useRosterDraft } from "./useRosterDraft";
import { plannerAPI } from "../../../api/client";
import RosterHeader from "./RosterHeader";
import RosterRow from "./RosterRow";
import RosterLegend from "./RosterLegend";
import RosterPalette from "./RosterPalette";
import CellPopover from "./CellPopover";
import { exportRosterImage, exportRosterPdf } from "./rosterExport";
import "./RosterGrid.css";

function monthIndex(y, m) { return y * 12 + m; }

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_NAMES_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAY_LABELS = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const DAY_KEYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function loadSavedMonth() {
  try {
    const raw = localStorage.getItem("roster-grid/month");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveMonth(year, month) {
  try { localStorage.setItem("roster-grid/month", JSON.stringify({ year, month })); } catch {}
}

function todayDateStr() {
  return toDateStr(new Date());
}

export default function RosterGrid() {
  const savedMonth = loadSavedMonth();
  const now = new Date();
  const [year, setYear] = useState(savedMonth?.year || now.getFullYear());
  const [month, setMonth] = useState(savedMonth?.month || now.getMonth() + 1);
  const [density, setDensity] = useState("compact");
  const [showExceptions, setShowExceptions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Selection state for range painting
  const [dragState, setDragState] = useState(null); // { userId, startDate, currentDate }
  const [committedSel, setCommittedSel] = useState(null); // { userId, startDate, endDate }
  const [anchorCell, setAnchorCell] = useState(null); // for shift-click

  // Focused cell for keyboard nav
  const [focusedCell, setFocusedCell] = useState(null); // { userId, dateStr }

  // Paint brush
  const [paintBrush, setPaintBrush] = useState(null); // null | { kind: 'shift', shift } | { kind: 'wo'|'leave'|'half'|'working'|'clear' }

  // Popover
  const [popover, setPopover] = useState(null); // { rect, userIds: string[], dates: string[] }

  const cellRefs = useRef(new Map());
  const tableRef = useRef(null);

  const { teamData, shiftTemplates, orgWorkingDays, loading, error, applyShift, applyOrgDefault, applyDayOverride, clearShift, refresh } =
    useRosterData(year, month);

  const { draft, isDraftMode, isCopying, isApplying, applyProgress, copyLastMonth, applyDraft, discardDraft, setCellDraft, getCellDraft } =
    useRosterDraft(year, month, shiftTemplates, orgWorkingDays);

  const dates = useMemo(() => monthDates(year, month), [year, month]);
  const todayStr = todayDateStr();

  const users = teamData?.users || [];

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setPaintBrush(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const applyBrushRange = useCallback(
    async (userId, startDate, endDate) => {
      if (!paintBrush) return;
      const rangeDates = dates.filter((d) => d >= startDate && d <= endDate);

      if (paintBrush.kind === "shift") {
        if (isDraftMode) {
          rangeDates.forEach((d) =>
            setCellDraft(userId, d, { type: "shift", shiftId: paintBrush.shift.id, shift: paintBrush.shift }),
          );
        } else {
          try { await applyShift(userId, startDate, endDate, paintBrush.shift); } catch {}
          await refresh();
        }
      } else if (paintBrush.kind === "clear") {
        for (const d of rangeDates) {
          try { await clearShift(userId, d); } catch {}
        }
        await refresh();
      } else {
        const overrideType =
          paintBrush.kind === "wo" ? "WEEKLY_OFF"
          : paintBrush.kind === "leave" ? "OFF"
          : paintBrush.kind === "half" ? "HALF_DAY"
          : paintBrush.kind === "working" ? "WORKING"
          : null;
        for (const d of rangeDates) {
          try { await applyDayOverride(userId, d, overrideType); } catch {}
        }
        await refresh();
      }
      setCommittedSel(null);
    },
    [paintBrush, isDraftMode, dates, setCellDraft, applyShift, applyDayOverride, clearShift, refresh],
  );

  // Navigation bounds: last month, present, next month only
  const nowIdx = monthIndex(new Date().getFullYear(), new Date().getMonth() + 1);
  const curIdx = monthIndex(year, month);
  const canGoPrev = curIdx > nowIdx - 1;
  const canGoNext = curIdx < nowIdx + 1;

  const navigate = (dir) => {
    let m = month + dir, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setYear(y); setMonth(m);
    saveMonth(y, m);
    setCommittedSel(null); setDragState(null);
  };

  const goToday = () => {
    const n = new Date();
    setYear(n.getFullYear()); setMonth(n.getMonth() + 1);
    saveMonth(n.getFullYear(), n.getMonth() + 1);
  };

  const handleExport = useCallback(async (format = "xlsx") => {
    setIsExporting(true);
    const filename = `schedule-${year}-${String(month).padStart(2, "0")}`;
    try {
      if (format === "xlsx") {
        const res = await plannerAPI.exportTeam({ year, month });
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }

      // Drop selection/popover highlights so they don't end up in the snapshot
      setPopover(null);
      setCommittedSel(null);
      setDragState(null);
      setFocusedCell(null);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const opts = { title: `Roster — ${MONTH_NAMES[month - 1]} ${year}`, filename, format };
      if (format === "pdf") await exportRosterPdf(tableRef.current, opts);
      else await exportRosterImage(tableRef.current, opts);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [year, month]);

  // Determine which dates are "selected" for a given user
  const getSelectedDates = useCallback(
    (userId) => {
      const active = dragState?.userId === userId ? dragState : null;
      const sel = committedSel?.userId === userId ? committedSel : null;
      const range = active || sel;
      if (!range) return null;
      const [a, b] = [range.startDate, range.currentDate || range.endDate].sort();
      const set = new Set();
      dates.forEach((d) => { if (d >= a && d <= b) set.add(d); });
      return set;
    },
    [dragState, committedSel, dates],
  );

  const openPopover = useCallback((rect, userIds, dates) => {
    setPopover({ rect, userIds, dates });
  }, []);

  const closePopover = useCallback(() => setPopover(null), []);

  const handleApplyShift = useCallback(
    async (shift) => {
      if (!popover) return;
      closePopover();
      const { userIds, dates: popDates } = popover;
      const startDate = popDates[0];
      const endDate = popDates[popDates.length - 1];

      if (isDraftMode) {
        for (const uid of userIds) {
          popDates.forEach((d) => setCellDraft(uid, d, { type: "shift", shiftId: shift.id, shift }));
        }
        return;
      }

      for (const uid of userIds) {
        try { await applyShift(uid, startDate, endDate, shift); } catch {}
      }
      await refresh();
      setCommittedSel(null);
    },
    [popover, isDraftMode, setCellDraft, applyShift, refresh, closePopover],
  );

  const handleApplyOverride = useCallback(
    async (type) => {
      if (!popover) return;
      closePopover();
      const { userIds, dates: popDates } = popover;

      if (isDraftMode) {
        for (const uid of userIds) {
          popDates.forEach((d) => setCellDraft(uid, d, { type: type.toLowerCase() }));
        }
        return;
      }

      for (const uid of userIds) {
        for (const d of popDates) {
          try { await applyDayOverride(uid, d, type); } catch {}
        }
      }
      await refresh();
      setCommittedSel(null);
    },
    [popover, isDraftMode, setCellDraft, applyDayOverride, refresh, closePopover],
  );

  const handleClear = useCallback(async () => {
    if (!popover) return;
    closePopover();
    const { userIds, dates: popDates } = popover;

    if (isDraftMode) {
      for (const uid of userIds) {
        popDates.forEach((d) => setCellDraft(uid, d, null));
      }
      return;
    }

    for (const uid of userIds) {
      for (const d of popDates) {
        try { await clearShift(uid, d); } catch {}
      }
    }
    await refresh();
    setCommittedSel(null);
  }, [popover, isDraftMode, setCellDraft, clearShift, refresh, closePopover]);

  const handleCellMouseDown = useCallback((e, userId, dateStr) => {
    if (e.button !== 0) return;
    e.preventDefault();
    if (!paintBrush && e.shiftKey && anchorCell?.userId === userId) {
      // Range extend via shift-click (normal mode only)
      const [a, b] = [anchorCell.dateStr, dateStr].sort();
      setCommittedSel({ userId, startDate: a, endDate: b, currentDate: b });
      return;
    }
    setAnchorCell({ userId, dateStr });
    setDragState({ userId, startDate: dateStr, currentDate: dateStr });
    setCommittedSel(null);
  }, [paintBrush, anchorCell]);

  const handleCellMouseEnter = useCallback((userId, dateStr) => {
    setDragState((prev) => {
      if (!prev || prev.userId !== userId) return prev;
      return { ...prev, currentDate: dateStr };
    });
  }, []);

  const handleCellClick = useCallback((e, userId, dateStr) => {
    if (paintBrush) return; // mouseup already applied the brush
    const rect = e.currentTarget.getBoundingClientRect();
    const sel = getSelectedDates(userId);
    const selectedArr = sel ? [...sel].sort() : [dateStr];

    // If clicked same cell that was already committed as single cell, show popover
    if (committedSel?.userId === userId && sel?.size === 1 && sel.has(dateStr)) {
      openPopover(rect, [userId], selectedArr);
      return;
    }

    // Single cell click — commit selection and show popover
    setCommittedSel({ userId, startDate: dateStr, endDate: dateStr, currentDate: dateStr });
    setDragState(null);
    setFocusedCell({ userId, dateStr });
    openPopover(rect, [userId], [dateStr]);
  }, [committedSel, getSelectedDates, openPopover]);

  const handleMouseUp = useCallback((userId, dateStr) => {
    if (!dragState) return;
    if (dragState.userId !== userId) { setDragState(null); return; }
    const [start, end] = [dragState.startDate, dragState.currentDate].sort();

    if (paintBrush) {
      setDragState(null);
      applyBrushRange(userId, start, end);
      return;
    }

    const isRange = start !== end;
    const committed = { userId, startDate: start, endDate: end, currentDate: end };
    setCommittedSel(committed);
    setDragState(null);

    if (isRange) {
      const el = cellRefs.current.get(`${userId}:${end}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        const selDates = dates.filter((d) => d >= start && d <= end);
        openPopover(rect, [userId], selDates);
      }
    }
  }, [paintBrush, applyBrushRange, dragState, dates, openPopover]);

  const handleCellKeyDown = useCallback((e, userId, dateStr) => {
    const userIdx = users.findIndex((u) => u.userId === userId);
    const dateIdx = dates.indexOf(dateStr);

    const move = (newUserId, newDate) => {
      e.preventDefault();
      setFocusedCell({ userId: newUserId, dateStr: newDate });
      const el = cellRefs.current.get(`${newUserId}:${newDate}`);
      el?.focus();
    };

    if (e.key === "ArrowRight" && dateIdx < dates.length - 1) move(userId, dates[dateIdx + 1]);
    else if (e.key === "ArrowLeft" && dateIdx > 0) move(userId, dates[dateIdx - 1]);
    else if (e.key === "ArrowDown" && userIdx < users.length - 1) move(users[userIdx + 1].userId, dateStr);
    else if (e.key === "ArrowUp" && userIdx > 0) move(users[userIdx - 1].userId, dateStr);
    else if (e.key === "Enter") {
      const el = cellRefs.current.get(`${userId}:${dateStr}`);
      if (el) openPopover(el.getBoundingClientRect(), [userId], [dateStr]);
    }
    else if (e.key === "Escape") { setCommittedSel(null); setDragState(null); closePopover(); }
  }, [users, dates, openPopover, closePopover]);

  const handleApplyDraft = useCallback(() => {
    applyDraft(() => refresh());
  }, [applyDraft, refresh]);

  // "Show exceptions" dims cells matching the user's dominant shift
  const dominantShiftByUser = useMemo(() => {
    if (!showExceptions) return null;
    const map = new Map();
    for (const user of users) {
      const counts = new Map();
      for (const day of Object.values(user.days || {})) {
        if (day.type === "WORKING") {
          const key = `${day.workStartTime}|${day.workEndTime}`;
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      }
      let best = null, bestCount = 0;
      counts.forEach((c, k) => { if (c > bestCount) { bestCount = c; best = k; } });
      map.set(user.userId, best);
    }
    return map;
  }, [showExceptions, users]);

  if (loading) return <div className="small-muted roster-loading">Loading roster…</div>;
  if (error) return <div className="small-muted" style={{ color: "#b91c1c" }}>{error}</div>;

  return (
    <div className={`roster-container density-${density}${paintBrush ? " is-painting" : ""}`}>
      <RosterHeader
        year={year} month={month}
        isDraftMode={isDraftMode}
        isCopying={isCopying}
        isApplying={isApplying}
        isExporting={isExporting}
        applyProgress={applyProgress}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        onPrev={() => navigate(-1)}
        onNext={() => navigate(1)}
        onToday={goToday}
        onCopyLastMonth={copyLastMonth}
        onApplyDraft={handleApplyDraft}
        onDiscardDraft={discardDraft}
        onExport={handleExport}
        density={density}
        onDensityToggle={() => setDensity((d) => d === "compact" ? "comfortable" : "compact")}
      />

      <RosterPalette
        shiftTemplates={shiftTemplates}
        paintBrush={paintBrush}
        onSelect={setPaintBrush}
      />

      {isDraftMode && (
        <div className="roster-draft-banner">
          Draft — unsaved changes. Apply to commit or Discard to revert.
        </div>
      )}

      <div className="roster-body">
        <div
          className="roster-wrapper"
          onMouseUp={(e) => {
            if (dragState) {
              const el = document.elementFromPoint(e.clientX, e.clientY);
              const cell = el?.closest("[role='gridcell']");
              const row = cell?.closest("tr");
              // try to get userId/date from focused or dragState
              handleMouseUp(dragState.userId, dragState.currentDate);
            }
          }}
        >
          <table ref={tableRef} className="roster-table" role="grid" aria-label="Monthly roster">
            <thead>
              <tr>
                <th className="roster-corner" />
                {dates.map((dateStr) => {
                  const d = new Date(dateStr);
                  const dayNum = d.getDate();
                  const weekday = WEEKDAY_LABELS[d.getDay()];
                  const isToday = dateStr === todayStr;
                  const isWeekend = !orgWorkingDays?.includes(DAY_KEYS[d.getDay()]);
                  return (
                    <th
                      key={dateStr}
                      className={`roster-date-header ${isToday ? "is-today" : ""} ${isWeekend ? "is-weekend" : ""}`}
                    >
                      <span className="date-num">{dayNum}</span>
                      <span className="date-wd">{weekday}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <RosterRow
                  key={user.userId}
                  user={user}
                  dates={dates}
                  days={user.days}
                  shiftTemplates={shiftTemplates}
                  orgWorkingDays={orgWorkingDays}
                  draftCells={draft?.[user.userId]}
                  isDraftMode={isDraftMode}
                  focusedDate={focusedCell?.userId === user.userId ? focusedCell.dateStr : null}
                  selectedDates={getSelectedDates(user.userId)}
                  onCellMouseDown={handleCellMouseDown}
                  onCellMouseEnter={handleCellMouseEnter}
                  onCellClick={handleCellClick}
                  onCellKeyDown={handleCellKeyDown}
                  todayStr={todayStr}
                  cellRefs={cellRefs}
                />
              ))}
            </tbody>
          </table>
        </div>

        <RosterLegend
          teamData={teamData}
          shiftTemplates={shiftTemplates}
          showExceptions={showExceptions}
          onToggleExceptions={() => setShowExceptions((v) => !v)}
        />
      </div>

      {popover && (
        <CellPopover
          anchorRect={popover.rect}
          shiftTemplates={shiftTemplates}
          onApplyShift={handleApplyShift}
          onApplyOverride={handleApplyOverride}
          onClear={handleClear}
          onClose={closePopover}
        />
      )}


    </div>
  );
}
